import { useEffect } from 'react';
import { ACTIONS, RESOURCES } from '@/contexts/CasbinContext';
import { UnlockFlow } from '@/hooks/useActionGuard';
import { useActionGuardConstraints } from '@/hooks/actionGuardConstraints';
import { useUserContext } from '@/modules/user/useUserContext';

/**
 * Registers the accountability precondition for sharing: a user may only
 * `share:action` once they've given the familiar name that identifies them to
 * the contacts they text. Their allowed name and preferred pronouns are
 * optional — a reshare story refers to a sender who withheld them as
 * "someone" or "they". This is an extra client-side claim layered on top of the
 * Casbin `share:action` check — when unmet, guards send the user to the Profile
 * Wizard Attribution step to gain it.
 *
 * Renders nothing; mounted once under UserProvider + ActionGuardConstraintsProvider.
 */
const ProfileAttributionShareConstraint: React.FC = () => {
  const { familiarName } = useUserContext();
  const { registerExtraRequiredClaim } = useActionGuardConstraints();

  const hasAttribution = Boolean(familiarName);

  useEffect(
    () =>
      registerExtraRequiredClaim(ACTIONS.SHARE, RESOURCES.TOPICS, () =>
        hasAttribution
          ? null
          : {
              key: 'profile_attribution',
              unlockFlow: UnlockFlow.ProfileWizardAttribution,
              helpText:
                'Provide your name to share this topic [Jump to My Profile]',
              returnFromUnlockText: 'Back to sharing',
            }
      ),
    [hasAttribution, registerExtraRequiredClaim]
  );

  return null;
};

export default ProfileAttributionShareConstraint;
